import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

const SANDSTONE = 0xf0d8b0;
const SAND_ROCK = 0xe8cc9c;

/** Sun-bleached sandstone kit (light stone walls tinted warm, broken columns). */
const KIT: DungeonKit = {
  wall: { model: 'wall_segment', length: 8, depth: 1.2, scale: 0.7, tint: SANDSTONE },
  post: { model: 'pillar_ruin', every: 3, scale: [1.15, 1.4], tint: SANDSTONE },
  light: { model: 'brazier', every: 3 },
  low: [
    { model: 'pillar_ruin', scale: [0.5, 0.7], tint: SANDSTONE, weight: 2 },
    { model: 'rock_b', scale: [0.7, 1.1], tint: SAND_ROCK },
    { model: 'bones' },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.45, 0.8], tint: SAND_ROCK, weight: 3 },
    { model: 'rock_c', r: 0.7, scale: [1.0, 1.8], tint: SAND_ROCK },
    { model: 'rock_a', r: 1.1, scale: [1.1, 1.8], tint: SAND_ROCK },
  ],
  clutter: [{ model: 'bones', weight: 3 }, { model: 'rock_b', scale: [0.3, 0.5], tint: SAND_ROCK, weight: 2 }],
  clutterChance: 0.3,
};

/**
 * SANDSWORN TOMB (Act II story dungeon, levels 17–19). A sunken stair drops
 * into a pillared processional gallery lined with sarcophagi, the cross-shaped
 * Hall of Scales (two dark burial chambers off-path), a winding descent and
 * the Warden's vestibule, ending in the round Sanctum (r 17) of the Sandbound
 * Warden. A paved runner marks the critical path through the drifted sand.
 *
 * Spawns: `start`, `boss`. Exits: `tomb_exit` by the start and
 * `tomb_exit_boss` in the sanctum (hidden until `tomb_cleared`), both to
 * Sunscar `fromTomb`.
 */
export function buildTomb(): ZoneLayout {
  const rng = new Rng('tomb');
  const d = new DungeonBuilder(
    {
      id: 'tomb', name: 'Sandsworn Tomb', subtitle: 'Where the Warden keeps the second shard', kind: 'dungeon', biome: 'abyss',
      levelRange: [17, 19], music: 'dungeon', width: 170, depth: 216, seed: 2301,
      ground: ['sand', 'tiles', 'rock', 'dirt'], groundScale: [5, 4, 7, 5], ambient: 'ash', mapTint: 0x3a2e20,
      wallHeight: 7, nearWallHeight: 1.1, accentAmount: 0.5, kit: KIT,
    },
    rng,
  );

  d.hall(0, 88, 6, 9, { y: 1.0, path: true }); // sunken stair
  d.hall(0, 56, 7.5, 20, { y: 0.4, path: true }); // processional gallery
  d.room(0, 18, 13, { y: -0.3, path: true }); // hall of scales
  d.hall(-27, 18, 6, 5, { y: -0.4 }); // west burial chamber (off-path)
  d.hall(27, 18, 6, 5, { y: -0.4 }); // east burial chamber (off-path)
  d.hall(3, -44, 10, 7, { y: -1.2, path: true }); // warden's vestibule
  d.room(3, -78, 17, { y: -2, path: true }); // sanctum
  d.corridor([{ x: 0, z: 81 }, { x: 0, z: 74 }], 6, { path: true });
  d.corridor([{ x: 0, z: 38 }, { x: 0, z: 29 }], 6, { path: true });
  d.corridor([{ x: -11, z: 18 }, { x: -22, z: 18 }], 4);
  d.corridor([{ x: 11, z: 18 }, { x: 22, z: 18 }], 4);
  d.corridor([{ x: 0, z: 7 }, { x: 9, z: -6 }, { x: -5, z: -20 }, { x: 3, z: -38 }], 5, { path: true });
  d.corridor([{ x: 3, z: -49 }, { x: 3, z: -63 }], 6, { path: true });
  d.runner([{ x: 0, z: 94 }, { x: 0, z: 74 }, { x: 0, z: 38 }, { x: 0, z: 29 }, { x: 0, z: 7 }, { x: 9, z: -6 }, { x: -5, z: -20 }, { x: 3, z: -38 }, { x: 3, z: -63 }, { x: 3, z: -70 }], 2.8);

  // Sunken stair (entry).
  d.spawn('start', 0, 86, Math.PI);
  d.litPortal({ id: 'tomb_exit', to: 'sunscar', toSpawn: 'fromTomb', x: 0, z: 94, radius: 2.2, label: 'Sunscar Expanse', kind: 'exit' }, 0);
  d.prop('statue_hero', -4.4, 83, { scale: 0.5, rot: Math.PI / 2, collider: { w: 2.6, d: 2.6 }, tint: SANDSTONE });
  d.prop('statue_hero', 4.4, 83, { scale: 0.5, rot: -Math.PI / 2, collider: { w: 2.6, d: 2.6 }, tint: SANDSTONE });

  // Processional gallery: columns and sarcophagi in the aisles.
  for (const z of [42, 49, 56, 63, 70]) {
    for (const x of [-4.6, 4.6]) d.prop(z === 56 && x > 0 ? 'pillar_ruin' : 'pillar_intact', x, z, { collider: { r: 0.6 }, tint: SANDSTONE, rot: z });
  }
  for (const z of [45.5, 52.5, 59.5, 66.5]) {
    for (const x of [-6.3, 6.3]) d.prop('sarcophagus', x, z, { collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  }
  d.arch(0, 75.5, Math.PI, 6, SANDSTONE);
  d.pack({ id: 'tomb_gallery_bones', monsters: [{ def: 'sunscar_skeleton', count: 4 }], x: 0, z: 64, radius: 3.5, level: 17 });
  d.pack({ id: 'tomb_gallery_ghouls', monsters: [{ def: 'tomb_ghoul', count: 3 }], x: 0, z: 46, radius: 3.5, level: 17 });

  // Hall of Scales: four guardian statues, burial chambers east and west.
  for (const h of [1.5, 4.5, 7.5, 10.5]) {
    const p = DungeonBuilder.clock(0, 18, 9.5, h);
    d.prop('statue_hero', p.x, p.z, { scale: 0.55, rot: Math.atan2(-p.x, 18 - p.z), collider: { w: 2.6, d: 2.6 }, tint: SANDSTONE });
  }
  d.paintCircle(0, 18, 6, 1);
  d.pack({ id: 'tomb_scales', monsters: [{ def: 'sand_golem', count: 1 }, { def: 'sunscar_skeleton', count: 3 }], x: 0, z: 17, radius: 5.5, level: 18 });
  d.interact({ id: 'tomb_chest_west', kind: 'chest', x: -30, z: 18, data: { loot: 'tomb_chest' } });
  d.prop('sarcophagus', -27, 14.6, { rot: Math.PI / 2, collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  d.prop('sarcophagus', -27, 21.4, { rot: Math.PI / 2, collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  d.interact({ id: 'tomb_lore_scales', kind: 'lore', x: 28, z: 20.5, data: { lore: 'tomb_scales' } });
  d.prop('altar', 30.5, 14.6, { collider: { w: 2.2, d: 1.2 }, tint: SANDSTONE });
  d.prop('sarcophagus', 25, 14.6, { rot: Math.PI / 2, collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  d.pack({ id: 'tomb_east_crypt', monsters: [{ def: 'tomb_ghoul', count: 2 }], x: 26, z: 18, radius: 2.5, level: 18 });

  // Winding descent.
  d.pack({ id: 'tomb_descent', monsters: [{ def: 'tomb_ghoul', count: 3 }], x: -3, z: -18, radius: 2, level: 18 });

  // Warden's vestibule: statues flank the sanctum door.
  d.prop('statue_hero', -2, -49, { scale: 0.55, collider: { w: 2.6, d: 2.6 }, tint: SANDSTONE });
  d.prop('statue_hero', 8, -49, { scale: 0.55, collider: { w: 2.6, d: 2.6 }, tint: SANDSTONE });
  d.prop('sarcophagus', 11, -40, { collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  d.prop('sarcophagus', -5, -40, { collider: { w: 1.2, d: 2.5 }, tint: SANDSTONE });
  d.pack({ id: 'tomb_vestibule', monsters: [{ def: 'sand_golem', count: 1 }, { def: 'tomb_ghoul', count: 2 }, { def: 'sunscar_skeleton', count: 2 }], x: 3, z: -43, radius: 5, level: 19 });
  d.arch(3, -61.5, Math.PI, 6, SANDSTONE);

  // Sanctum of the Sandbound (r 17).
  d.paintCircle(3, -78, 8.5, 1);
  d.paintRing(3, -78, 12.6, 13.6, 3);
  d.spawn('boss', 3, -80, 0);
  d.pack({ id: 'tomb_boss', monsters: [{ def: 'sandbound_warden', count: 1 }], x: 3, z: -80, radius: 1, level: 19, tags: ['boss'] });
  d.litPortal({ id: 'tomb_exit_boss', to: 'sunscar', toSpawn: 'fromTomb', x: 3, z: -91.5, radius: 2.4, label: 'Return to Sunscar', kind: 'exit', requires: 'tomb_cleared' }, Math.PI);
  d.prop('sarcophagus', 3, -99, { scale: 1.7, rot: Math.PI / 2, tint: 0xffe0a0 });
  for (const h of [9.5, 2.5]) {
    const p = DungeonBuilder.clock(3, -78, 18.6, h);
    d.prop('statue_hero', p.x, p.z, { scale: 0.75, rot: Math.atan2(3 - p.x, -78 - p.z), tint: SANDSTONE });
  }

  return d.build();
}
